import type { PropsWithSpread } from "@canonical/react-components";
import { createWindowConfigResolver } from "@portal/utilities/config";
import { expectNonEmptyString } from "@portal/utilities/guards";
import { createJsonClient, type JsonClient } from "@portal/utilities/http";
import { TRACEPARENT_HEADER_NAME } from "@portal/utilities/logging/trace";
import { normalizeFqdn, normalizeRoot } from "@portal/utilities/normalizer";

export type FrontendConfig = {
  root: string;
  janusRoot: string;
  janusLoginUrl: string;
  gatewayUrl: string;
  traceHeaderName: string;
  sentry: {
    dsn: string;
    enabled: boolean;
    environment: string;
    tracesSampleRate: number;
  };
  api?: JsonClient;
};

type ZeusWindowConfig = PropsWithSpread<
  {
    root: string;
  },
  Partial<FrontendConfig>
>;

/**
 * Resolves the frontend configuration injected by the backend during SSR.
 * Throws when the configuration has not been provided to help detect misconfigurations early.
 */
const resolveFrontendConfig = createWindowConfigResolver<ZeusWindowConfig>({
  normalize: (config) => {
    const root = normalizeRoot(config.root);
    const janusRoot = normalizeRoot(config.janusRoot, `authentication`);
    const janusLoginUrl = expectNonEmptyString(config.janusLoginUrl, `Zeus Janus login URL is not configured`);
    const gatewayUrl = normalizeFqdn(expectNonEmptyString(config.gatewayUrl, `Zeus gateway URL is not configured`));
    const prefix = `${gatewayUrl}`;
    return {
      ...config,
      root,
      janusRoot,
      janusLoginUrl,
      gatewayUrl,
      traceHeaderName: TRACEPARENT_HEADER_NAME,
      sentry: {
        dsn: config.sentry?.dsn ?? ``,
        enabled: config.sentry?.enabled ?? false,
        environment: config.sentry?.environment ?? `development`,
        tracesSampleRate: config.sentry?.tracesSampleRate ?? 0.1,
      },
      api: createJsonClient({ prefix }),
    };
  },
});
const resolvedConfig = resolveFrontendConfig();

const resolvedRoot = expectNonEmptyString(resolvedConfig.root, `Zeus frontend root is not configured`);
const resolvedJanusRoot = expectNonEmptyString(resolvedConfig.janusRoot, `Zeus Janus root is not configured`);
const resolvedJanusLoginUrl = expectNonEmptyString(resolvedConfig.janusLoginUrl, `Zeus Janus login URL is not configured`);
const resolvedGatewayUrl = expectNonEmptyString(resolvedConfig.gatewayUrl, `Zeus gateway URL is not configured`);
const resolvedSentry: FrontendConfig[`sentry`] = {
  dsn: resolvedConfig.sentry?.dsn ?? ``,
  enabled: resolvedConfig.sentry?.enabled ?? false,
  environment: resolvedConfig.sentry?.environment ?? `development`,
  tracesSampleRate: resolvedConfig.sentry?.tracesSampleRate ?? 0.1,
};
const apiClient = resolvedConfig.api;
if (!apiClient || typeof apiClient.fetchJson !== `function` || typeof apiClient.sendJson !== `function` || typeof apiClient.buildQueryString !== `function`) {
  throw new Error(`Zeus API client is not configured`);
}

export const config: FrontendConfig & { api: JsonClient } = {
  root: resolvedRoot,
  janusRoot: resolvedJanusRoot,
  janusLoginUrl: resolvedJanusLoginUrl,
  gatewayUrl: resolvedGatewayUrl,
  traceHeaderName: TRACEPARENT_HEADER_NAME,
  sentry: resolvedSentry,
  api: apiClient,
};

Object.freeze(apiClient);
Object.freeze(config);

export const root = config.root;
export const janusRoot = config.janusRoot;
export const janusLoginUrl = config.janusLoginUrl;
export const gatewayUrl = config.gatewayUrl;
export const traceHeaderName = config.traceHeaderName;
export const sentry = config.sentry;
export const fetchJson = config.api.fetchJson;
export const sendJson = config.api.sendJson;
export const buildQueryString = config.api.buildQueryString;
